/**
 * 配色テーマ (ダーク / ライト / システム) の唯一の実装。
 *
 * ## どこにも `document.documentElement.classList.add("dark")` を書かない
 * テーマの正体は `<html>` に付く `dark` / `light` クラス 1 つだけで、
 * 実際の色は `app/globals.css` の `:root, .dark` と `.light` が持つ。
 * クラスの付け外しは **この関数 (`applyColorTheme`) だけ**が行う。
 * 画面ごとに classList を触ると、テーマを切り替えた瞬間だけ直る/戻らない
 * といった再現しづらいバグになる。
 *
 * ## 保存先が localStorage ではなく AppSettings な理由
 * localStorage は**オリジンが別**(`https://donezo...` と `tauri://localhost`)
 * なので、Web で選んだテーマがデスクトップアプリに引き継がれない。
 * 表示倍率 (lib/display-zoom.ts) と同じ理由で正は `AppSettings.colorTheme` に置き、
 * localStorage はペイント前復元のためのミラーとしてだけ使う。
 */

import type { ColorTheme } from "@/lib/types";

export type { ColorTheme };

/** 実際に DOM へ当てる値 (`system` を解決した後) */
export type ResolvedColorTheme = "dark" | "light";

export const COLOR_THEMES: readonly ColorTheme[] = ["dark", "light", "system"];

/** 既存ユーザーの見え方を変えないため、既定は従来どおりダーク */
export const DEFAULT_COLOR_THEME: ColorTheme = "dark";

/** 設定画面のセグメント選択に出す並び */
export const COLOR_THEME_PRESETS: readonly {
  value: ColorTheme;
  label: string;
  description: string;
}[] = [
  { value: "dark", label: "ダーク", description: "既定" },
  { value: "light", label: "ライト", description: "明るい配色" },
  { value: "system", label: "システム", description: "OS に合わせる" },
];

/**
 * 設定より先に (= 最初のペイント前に) テーマを復元するためのローカルミラー。
 * 正は `AppSettings.colorTheme`。ここは起動直後のちらつき防止のキャッシュにすぎない。
 *
 * **`app/layout.tsx` のインラインスクリプトと同じ文字列を使うこと**
 * (`__tests__/lib/color-theme.test.ts` が両者の一致を検査している)。
 */
export const COLOR_THEME_MIRROR_KEY = "app-color-theme";

/** OS がライトを要求しているかを判定するメディアクエリ */
export const PREFERS_LIGHT_QUERY = "(prefers-color-scheme: light)";

/**
 * `<meta name="theme-color">` に入れる値。
 * モバイル Safari / Chrome のアドレスバーがここを見るので、
 * テーマを切り替えたらページの床と同じ色に揃える。
 */
export const THEME_COLOR_META: Record<ResolvedColorTheme, string> = {
  dark: "#0f1115",
  light: "#ffffff",
};

/** 設定値として不正・未設定なら既定 (ダーク) に丸める */
export function normalizeColorTheme(value: unknown): ColorTheme {
  return COLOR_THEMES.includes(value as ColorTheme)
    ? (value as ColorTheme)
    : DEFAULT_COLOR_THEME;
}

/** OS がライト配色を要求しているか。matchMedia が無い環境では false */
export function prefersLightColorScheme(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return window.matchMedia(PREFERS_LIGHT_QUERY).matches;
  } catch {
    return false;
  }
}

/**
 * `system` を OS の設定で解決する。
 * `prefersLight` を渡すとその値を使う (テスト・プリペイントスクリプト用)。
 */
export function resolveColorTheme(
  value: unknown,
  prefersLight: boolean = prefersLightColorScheme()
): ResolvedColorTheme {
  const theme = normalizeColorTheme(value);
  if (theme === "system") return prefersLight ? "light" : "dark";
  return theme;
}

/**
 * 実際に DOM へ反映する。`<html>` のクラスと `<meta name="theme-color">`、
 * それにミラーを更新して、解決後のテーマを返す。
 */
export function applyColorTheme(value: unknown): ResolvedColorTheme {
  const theme = normalizeColorTheme(value);
  const resolved = resolveColorTheme(theme);
  if (typeof document === "undefined") return resolved;

  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.classList.toggle("light", resolved === "light");

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR_META[resolved]);

  try {
    // ミラーには解決後ではなく**ユーザーの選択**を入れる。
    // `system` を `dark` に潰して保存すると、OS を切り替えても
    // 次回起動時にダークのまま貼り付いてしまう
    localStorage.setItem(COLOR_THEME_MIRROR_KEY, theme);
  } catch {
    // プライベートブラウジング等で localStorage が使えなくても致命的ではない
  }
  return resolved;
}

/** いま DOM に当たっているテーマ。クラスが無ければ既定 (ダーク) */
export function readAppliedColorTheme(): ResolvedColorTheme {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.classList.contains("light") ? "light" : "dark";
}

/** ミラーから起動時のテーマを読む。設定の読み込みが終わるまでの暫定値 */
export function readMirroredColorTheme(): ColorTheme | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(COLOR_THEME_MIRROR_KEY);
    if (raw === null) return null;
    return COLOR_THEMES.includes(raw as ColorTheme) ? (raw as ColorTheme) : null;
  } catch {
    return null;
  }
}

/**
 * OS の配色変更を購読する (`system` 選択中のみ使う)。
 * 解除用の関数を返す。購読できない環境では何もしない関数を返す。
 */
export function subscribeToSystemColorScheme(
  onChange: (prefersLight: boolean) => void
): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => {};
  }
  let query: MediaQueryList;
  try {
    query = window.matchMedia(PREFERS_LIGHT_QUERY);
  } catch {
    return () => {};
  }
  const handler = (event: MediaQueryListEvent) => onChange(event.matches);

  // Safari 13 以前は addEventListener を持たない
  if (typeof query.addEventListener === "function") {
    query.addEventListener("change", handler);
    return () => query.removeEventListener("change", handler);
  }
  if (typeof query.addListener === "function") {
    query.addListener(handler);
    return () => query.removeListener(handler);
  }
  return () => {};
}
